import React from 'react';
import { Typography, styled, Button, useMediaQuery, useTheme, Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toEuroWholeNumber } from '../../utils/numberFormat';
import { CashbackAdvantage } from '../DetailCalculator';

const FlexRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: theme.spacing(3),

  [theme.breakpoints.down('md')]: {
    marginBottom: 0,
  },
}));

const BookNowButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 500,
}));

const ShowDetailsButton = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  cursor: 'pointer',
  color: theme.palette.text.primary,

  '&:hover': {
    color: theme.palette.text.primary,
    textDecoration: 'underline',
  },
}));

const Spacer = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(1),

  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(4),
  },
}));

interface Props {
  cashbackAdvantage: CashbackAdvantage;
  bookNowUrl: string;
  handleCalculateNowButtonClick: () => void;
}

export const AggregatedCommissionBox = ({
  cashbackAdvantage,
  bookNowUrl,
  handleCalculateNowButtonClick,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isLessMd = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box flexGrow="1" pb={isLessMd ? 6 : 0}>
      <FlexRow>
        <Typography
          variant="body1"
          component="div"
          color="textSecondary"
          sx={{
            fontSize: isLessMd ? '14px' : '16px',
          }}
        >
          {t('cashbackCalculator.detailed.aggregatedCommissionBox.refundableAmount')}
          &nbsp;&nbsp;
        </Typography>
        {cashbackAdvantage.totalPortfolioCommission ? (
          <Typography
            sx={{ fontSize: isLessMd ? '20px' : '28px', lineHeight: '34px' }}
            color="success.dark"
          >
            {toEuroWholeNumber(cashbackAdvantage.totalPortfolioCommission)}
          </Typography>
        ) : (
          <Typography
            sx={{ fontSize: isLessMd ? '20px' : '28px', lineHeight: '34px' }}
            color="common.N400"
          >
            -
          </Typography>
        )}
      </FlexRow>

      <FlexRow>
        <Typography
          variant="body1"
          component="div"
          color="textSecondary"
          sx={{
            fontSize: isLessMd ? '14px' : '16px',
          }}
        >
          {t('cashbackCalculator.detailed.aggregatedCommissionBox.totalAgio')}
          &nbsp;&nbsp;
        </Typography>
        {cashbackAdvantage.totalAgio ? (
          <Typography
            sx={{ fontSize: isLessMd ? '20px' : '28px', lineHeight: '34px' }}
            color="success.dark"
          >
            {toEuroWholeNumber(cashbackAdvantage.totalAgio)}
          </Typography>
        ) : (
          <Typography
            sx={{ fontSize: isLessMd ? '20px' : '28px', lineHeight: '34px' }}
            color="common.N400"
          >
            -
          </Typography>
        )}
      </FlexRow>

      <Spacer />

      <FlexRow>
        <BookNowButton
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          href={bookNowUrl}
          data-eventid="book-cashback-button"
        >
          {t('cashbackCalculator.detailed.advantageBox.applyButton')}
        </BookNowButton>
      </FlexRow>

      {!isLessMd && (
        <FlexRow sx={{ justifyContent: 'center' }}>
          <ShowDetailsButton>
            <Typography
              variant="body1"
              onClick={() => {
                handleCalculateNowButtonClick();
              }}
            >
              {t('cashbackCalculator.detailed.aggregatedCommissionBox.calculateCashback')}
            </Typography>
          </ShowDetailsButton>
        </FlexRow>
      )}
    </Box>
  );
};
